import { useCallback, useEffect, useMemo, useState } from "react";
import { loadTablePrefs, saveTablePrefs } from "../utils/tablePersonalize";

export function useTablePersonalize(tableId) {
  const [prefs, setPrefs] = useState(() => loadTablePrefs(tableId));

  useEffect(() => {
    setPrefs(loadTablePrefs(tableId));
  }, [tableId]);

  useEffect(() => {
    saveTablePrefs(tableId, prefs);
  }, [tableId, prefs]);

  const hidden = useMemo(() => new Set((prefs.hidden || []).map(String)), [prefs.hidden]);
  const labels = prefs.labels || {};

  const isOn = useCallback((id) => !hidden.has(String(id)), [hidden]);

  const header = useCallback(
    (id, fallback) => {
      const custom = String(labels[id] || "").trim();
      return custom || fallback;
    },
    [labels]
  );

  const toggle = useCallback((id, columns = []) => {
    const key = String(id);
    setPrefs((prev) => {
      const nextHidden = new Set((prev.hidden || []).map(String));
      if (nextHidden.has(key)) {
        nextHidden.delete(key);
      } else {
        nextHidden.add(key);
        const stillVisible = (columns || []).filter((c) => !nextHidden.has(String(c.id)));
        if (stillVisible.length === 0) nextHidden.delete(key);
      }
      return { ...prev, hidden: [...nextHidden] };
    });
  }, []);

  const rename = useCallback((id, label) => {
    const key = String(id);
    setPrefs((prev) => ({
      ...prev,
      labels: { ...(prev.labels || {}), [key]: label },
    }));
  }, []);

  const reset = useCallback(() => setPrefs({ hidden: [], labels: {} }), []);

  return { isOn, toggle, rename, reset, header, hidden, labels };
}
